import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import { ChatRequest, MessageRequest } from "../types";

const onCreateChat = async (body: ChatRequest) => {
  const { data } = await axios.post(`/chat`, body);
  return data;
};

export const useCreateChat = (
  invalidateKeys: (string | (string | boolean)[])[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onCreateChat,
    onSuccess: () => {
      invalidateKeys.forEach((key) => {
        if (typeof key === "string") {
          queryClient.invalidateQueries({ queryKey: [key] });
        } else {
          queryClient.invalidateQueries({ queryKey: key });
        }
      });
    },
  });
};

const onCreateMessage = async (body: MessageRequest) => {
  const { data } = await axios.post(`/chat/message`, body);
  return data;
};

export const useCreateMessage = (
  invalidateKeys: (string | (string | boolean)[])[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onCreateMessage,
    onSuccess: () => {
      invalidateKeys.forEach((key) => {
        if (typeof key === "string") {
          queryClient.invalidateQueries({ queryKey: [key] });
        } else {
          queryClient.invalidateQueries({ queryKey: key });
        }
      });
    },
  });
};

const onGetChats = async (type: string) => {
  const { data } = await axios.get(`/chat/${type}`);
  return data;
};

export const useChats = (isBuy: boolean) =>
  useQuery({
    queryKey: [queryKeys.CHATS],
    queryFn: () => {
      let type = "buyer";
      if (!isBuy) type = "seller";
      return onGetChats(type);
    },
  });

const onGetOrderChat = async (orderId: string) => {
  const { data } = await axios.get(`/chat/order/${orderId}`);
  return data;
};

export const useOrderChat = (orderId: string, isMultiple?: boolean) =>
  useQuery({
    queryKey: [queryKeys.CHAT, orderId, isMultiple],
    queryFn: () => onGetOrderChat(orderId),
    enabled: !!orderId && !isMultiple,
    retry: false,
  });

const onGetMultipleOrderChat = async (multipleOrderId: string) => {
  const { data } = await axios.get(`/chat/multipleOrder/${multipleOrderId}`);
  return data;
};

export const useMultipleOrderChat = (
  multipleOrderId: string,
  isMultiple?: boolean
) =>
  useQuery({
    queryKey: [queryKeys.MULTIPLE_ORDER_CHAT, multipleOrderId, isMultiple],
    queryFn: () => onGetMultipleOrderChat(multipleOrderId),
    enabled: !!multipleOrderId && isMultiple,
    retry: false,
  });

const onGetChat = async (userId: string, materialId: string) => {
  const { data } = await axios.get(`/chat/${userId}/${materialId}`);
  return data;
};

export const useChat = (userId: string, materialId: string, isOpen?: boolean) =>
  useQuery({
    queryKey: [queryKeys.CHAT, isOpen, userId, materialId],
    queryFn: () => onGetChat(userId, materialId),
    enabled: !!userId && !!materialId && isOpen,
    retry: false,
  });
